import { useCallback, useEffect, useMemo, useState } from 'react';
import { CatalogItemKind, isSyntheticCatalogTrackMaid } from 'fym-shared/src/smartlinks/catalog';
import { resolveSmartLinkPlatformDomain } from 'fym-shared/src/smartlinks/domains';
import { SerializedSmartLinkVersionTheme } from 'fym-shared/src/smartlinks/types';
import { useGetAccountQuery, useGetCatalogQuery } from '../../../redux/api/fymAPI';
import { removeHttpAndSlash } from '../utils/removeHttpRegex';
import { applyPlatformDomainToPrefix } from '../utils/platformDomainPrefix';
import { CatalogItem } from '../../../redux/api/fymAPI.types';
import { MeiliTrackData } from '../../music24/components/SearchField/types';
import { useCatalogTrackResolution } from '../wizards/common/useCatalogTrackResolution';
import { useCatalogRowResolution } from '../wizards/common/useCatalogRowResolution';
import { usePastedUrlResolution } from '../wizards/common/usePastedUrlResolution';
import { emptyReleaseResolution, ReleaseResolution } from '../wizards/common/releaseResolutionTypes';
import { toReleaseDay } from '../wizards/common/toReleaseDay';
import { CreationPageType, inferPageType, PageTypeInference, todayUTC } from './inferPageType';
import { CreationSource, CreationStage } from './types';
import { CreationTemplateId } from './blankTemplates';
import { useCreationSlug } from './useCreationSlug';
import { useTrackCreateStarted } from './useTrackCreateStarted';
import { useCreationSearch } from './useCreationSearch';
import { looksLikeUrl, toResolvableUrl } from './looksLikeUrl';
import { ManualFields } from './CreationManualStage';
import { CreationDraft } from './props';
import { creationCoverUrl, liveCatalogRow } from './creationCover';
import { initialCreationSource } from './initialCreationSource';
import { useReleaseSmartLinkTheme } from '../releaseTheme/useReleaseSmartLinkTheme';

interface UseCreationSheetStateProps {
  accountId: number;
  initialCatalogTrackMaid?: string;
  initialCatalogItemUuid?: string;
}

const DEFAULT_THEME = SerializedSmartLinkVersionTheme.cassette;

/**
 * Everything the sheet knows, in one place.
 *
 * The only thing stored is what the artist put in. The stage, the page type,
 * the services and the address suggestion are all read off that plus whatever
 * the resolver has answered so far — which is why nothing has to be asked for
 * twice and why an override never has to be un-done.
 */
export function useCreationSheetState({
  accountId,
  initialCatalogTrackMaid,
  initialCatalogItemUuid,
}: UseCreationSheetStateProps) {
  const [now] = useState(() => Date.now());
  const today = useMemo(() => todayUTC(now), [now]);

  const { data: accountResponse } = useGetAccountQuery({ accountId });
  // The same arguments the Catalog screen uses, so the two share one entry.
  const { data: catalog } = useGetCatalogQuery({ accountId });
  const catalogItems = useMemo(() => catalog?.items ?? [], [catalog?.items]);

  const accountSlugPrefix = `${removeHttpAndSlash(accountResponse?.account.shareUrl ?? 'slug.fym.fm')}/`;
  const accountName = accountResponse?.account.name ?? '';
  const accountPlatformDomain = resolveSmartLinkPlatformDomain(null, accountResponse?.account.platformDomain);

  const [source, setSource] = useState<CreationSource>({ kind: 'none' });
  const [query, setQuery] = useState('');
  const [isDuplicateAccepted, setIsDuplicateAccepted] = useState(false);
  const [pageTypeOverride, setPageTypeOverride] = useState<CreationPageType>();
  const [releaseDate, setReleaseDate] = useState('');
  const [isDateTouched, setIsDateTouched] = useState(false);
  // Null until the artist picks a theme: the page then follows the release theme of the song.
  const [themeOverride, setThemeOverride] = useState<SerializedSmartLinkVersionTheme | null>(null);
  // Null until the artist moves the page off the account's host, so a page they
  // never thought about goes on following the account.
  const [platformDomainOverride, setPlatformDomain] = useState<string | null>(null);
  const [template, setTemplate] = useState(CreationTemplateId.empty);
  const [blankName, setBlankName] = useState('');
  const [manualFields, setManualFields] = useState<ManualFields>({
    title: '',
    artistName: '',
    releaseType: '',
  });

  const search = useCreationSearch(source.kind === 'none' ? query : '');

  // A Catalog row pressed "Create link", or the SmartLinks page opened with
  // `?catalogItemUuid=`, so the sheet opens on that release.
  useEffect(() => {
    const initial = initialCreationSource({ initialCatalogTrackMaid, initialCatalogItemUuid, catalogItems });
    if (initial !== undefined) {
      setSource(initial);
    }
  }, [catalogItems, initialCatalogItemUuid, initialCatalogTrackMaid]);

  const trackMaid = source.kind === 'catalogTrack' ? source.trackMaid : undefined;
  const pastedUrl = source.kind === 'url' ? source.url : undefined;
  // The row as the cache holds it now: a new cover (made in the editor tab) shows at once.
  const pickedRow = useMemo(
    () => (source.kind === 'catalogRow' ? liveCatalogRow(source.item, catalogItems) : undefined),
    [catalogItems, source]
  );
  const catalogResolution = useCatalogTrackResolution(trackMaid);
  const urlResolution = usePastedUrlResolution(pastedUrl);
  const rowResolution = useCatalogRowResolution(pickedRow);

  const resolution: ReleaseResolution = useMemo(() => {
    if (trackMaid !== undefined) {
      return catalogResolution;
    }
    if (pastedUrl !== undefined) {
      return urlResolution;
    }
    // A row of its own: an unreleased one resolves to nothing, and one claimed
    // by hand or off a profile resolves out of the links it carries.
    if (pickedRow !== undefined) {
      return rowResolution;
    }
    return emptyReleaseResolution;
  }, [catalogResolution, pastedUrl, pickedRow, rowResolution, trackMaid, urlResolution]);

  useTrackCreateStarted(source, resolution.main !== undefined);

  /** The catalog row behind whatever is being resolved, when there is one. */
  const catalogRow: CatalogItem | undefined = useMemo(() => {
    if (source.kind === 'catalogRow') {
      return pickedRow;
    }
    if (source.kind !== 'catalogTrack') {
      return undefined;
    }
    return catalogItems.find((item) => item.catalogTrackMaid === source.trackMaid);
  }, [catalogItems, pickedRow, source]);

  // A song with a release kit preselects the SmartLink theme of its release theme; the artist can
  // still pick another. A song with no kit keeps the default.
  const releaseTheme = useReleaseSmartLinkTheme(accountId, catalogRow?.uuid);
  const theme = themeOverride ?? releaseTheme ?? DEFAULT_THEME;

  /** The cover of the song: the catalog row first, then the picture of the resolution. */
  const coverUrl = useMemo(() => creationCoverUrl(catalogRow, resolution.imageUrl), [catalogRow, resolution.imageUrl]);

  /** The page this release already has, which is the whole duplicate guard. */
  const duplicateItem = catalogRow !== undefined && catalogRow.smartLinkId !== null ? catalogRow : undefined;

  const knownReleaseDate = useMemo(() => {
    const rowDate = catalogRow?.releaseDate ?? undefined;
    return rowDate === undefined ? resolution.releaseDate : toReleaseDay(rowDate);
  }, [catalogRow?.releaseDate, resolution.releaseDate]);

  const inference: PageTypeInference = useMemo(
    () =>
      inferPageType({
        catalogKind: catalogRow?.kind,
        releaseDate: knownReleaseDate,
        isResolved: resolution.main !== undefined,
        today,
      }),
    [catalogRow?.kind, knownReleaseDate, resolution.main, today]
  );

  const pageType = source.kind === 'manual' ? CreationPageType.presave : (pageTypeOverride ?? inference.type);

  // A date the artist has not touched follows the release, but only while it
  // is still ahead — a campaign for a day gone by has nobody left to notify.
  useEffect(() => {
    if (isDateTouched || knownReleaseDate === undefined || knownReleaseDate <= today) {
      return;
    }
    setReleaseDate(knownReleaseDate);
  }, [isDateTouched, knownReleaseDate, today]);

  /** What the address is suggested from, before the artist touches it. */
  const suggestedName = useMemo(() => {
    switch (source.kind) {
      case 'blank':
        return blankName;
      case 'manual':
        return manualFields.title;
      case 'catalogRow':
        return source.item.title;
      case 'catalogTrack':
        return resolution.title ?? source.title;
      case 'url':
        return resolution.title;
      case 'none':
      default:
        return undefined;
    }
  }, [blankName, manualFields.title, resolution.title, source]);

  const slug = useCreationSlug(accountId, suggestedName);

  const setTheme = useCallback((value: string) => setThemeOverride(value as SerializedSmartLinkVersionTheme), []);
  const setPageType = useCallback((value: CreationPageType) => setPageTypeOverride(value), []);
  const setDate = useCallback((value: string) => {
    setIsDateTouched(true);
    setReleaseDate(value);
  }, []);

  const platformDomain = platformDomainOverride ?? accountPlatformDomain;
  const slugPrefix = useMemo(
    () => applyPlatformDomainToPrefix(accountSlugPrefix, platformDomain),
    [accountSlugPrefix, platformDomain]
  );

  const draft: CreationDraft = useMemo(
    () => ({
      pageType,
      setPageType,
      releaseDate,
      setReleaseDate: setDate,
      theme,
      setTheme,
      slug,
      platformDomain,
      setPlatformDomain,
      platformDomainOverride,
    }),
    [pageType, platformDomain, platformDomainOverride, releaseDate, setDate, setPageType, setTheme, slug, theme]
  );

  // Nothing answered for a release the artist searched for. That is the normal
  // answer for something unreleased, so the sheet stops asking and takes their
  // word for it instead of dead-ending.
  useEffect(() => {
    if (!resolution.hasNoLinks) {
      return;
    }
    const fallbackQuery = source.kind === 'url' ? source.url : (resolution.title ?? query);
    setSource({ kind: 'manual', query: fallbackQuery });
    setManualFields((fields) => ({
      ...fields,
      title: fields.title === '' ? (resolution.title ?? query) : fields.title,
      artistName: fields.artistName === '' ? accountName : fields.artistName,
    }));
  }, [accountName, query, resolution.hasNoLinks, resolution.title, source]);

  const stage: CreationStage = useMemo(() => {
    switch (source.kind) {
      case 'blank':
        return CreationStage.blank;
      case 'manual':
        return CreationStage.manual;
      case 'catalogRow':
        return duplicateItem !== undefined && !isDuplicateAccepted ? CreationStage.duplicate : CreationStage.resolved;
      case 'catalogTrack':
      case 'url':
        if (duplicateItem !== undefined && !isDuplicateAccepted) {
          return CreationStage.duplicate;
        }
        return resolution.main === undefined ? CreationStage.resolving : CreationStage.resolved;
      case 'none':
      default:
        return CreationStage.start;
    }
  }, [duplicateItem, isDuplicateAccepted, resolution.main, source.kind]);

  const goToStart = useCallback(() => {
    setSource({ kind: 'none' });
    setIsDuplicateAccepted(false);
    setPageTypeOverride(undefined);
  }, []);

  const pickCatalogItem = useCallback((item: CatalogItem) => {
    setPageTypeOverride(undefined);
    if (
      item.kind === CatalogItemKind.unreleased ||
      item.catalogTrackMaid === null ||
      isSyntheticCatalogTrackMaid(item.catalogTrackMaid)
    ) {
      setSource({ kind: 'catalogRow', item });
      return;
    }
    setSource({ kind: 'catalogTrack', trackMaid: item.catalogTrackMaid, title: item.title });
  }, []);

  const pickTrack = useCallback((track: MeiliTrackData) => {
    setPageTypeOverride(undefined);
    setSource({ kind: 'catalogTrack', trackMaid: track.id, title: track.name, artistName: track.artistName });
  }, []);

  /** Enter in the omni field: a link resolves, anything else is filled in. */
  const submitQuery = useCallback(() => {
    const text = query.trim();
    if (text === '') {
      return;
    }
    setPageTypeOverride(undefined);
    if (looksLikeUrl(text)) {
      setSource({ kind: 'url', url: toResolvableUrl(text) });
      return;
    }
    setSource({ kind: 'manual', query: text });
    setManualFields((fields) => ({ ...fields, title: text, artistName: accountName }));
  }, [accountName, query]);

  const startBlank = useCallback(() => {
    setSource({ kind: 'blank' });
    setBlankName((name) => (name === '' ? query.trim() : name));
  }, [query]);

  const acceptDuplicate = useCallback(() => setIsDuplicateAccepted(true), []);

  const setManualField = useCallback((field: keyof ManualFields, value: string) => {
    setManualFields((fields) => ({ ...fields, [field]: value }));
  }, []);

  return {
    now,
    today,
    stage,
    source,
    query,
    setQuery,
    search,
    catalogItems,
    slugPrefix,
    accountName,
    resolution,
    catalogRow,
    coverUrl,
    duplicateItem,
    inference,
    draft,
    manualFields,
    setManualField,
    blankName,
    setBlankName,
    template,
    setTemplate,
    goToStart,
    pickCatalogItem,
    pickTrack,
    submitQuery,
    startBlank,
    acceptDuplicate,
  };
}
